import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { db } from '../firebase/config';
import { doc, getDoc, collection, addDoc, serverTimestamp, writeBatch, increment } from 'firebase/firestore';
import { MAX_FORM_RESPONSES } from '../utils/constants';
import { Form, FormPage, FormElement } from '../types/formTypes';
import { convertFirestoreTimestamps } from '../utils/firestore';
import PhoneInputField from '../components/PhoneInputField';
import 'react-phone-number-input/style.css';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

/**
 * PublicFormView component for public access to published forms
 * This component is accessible without authentication
 */


const PublicFormView: React.FC = () => {
  // Sajid //
const dateInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
// end //
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form | null>(null);
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isPreviewMobile, setIsPreviewMobile] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isQuotaExceeded, setIsQuotaExceeded] = useState(false);
  
  useEffect(() => {
    const loadPublishedForm = async () => {
      setIsLoading(true);
      try {
        if (!formId) {
          console.error('No form ID provided in URL');
          setLoadError('No form ID provided');
          setIsLoading(false);
          return;
        }

        //console.log(`Attempting to load public form with ID: ${formId}`);

        // Get the form directly from Firestore
        const formRef = doc(db, 'forms', formId);
        //console.log('Created Firestore reference:', formRef.path);
        
        try {
          const formSnap = await getDoc(formRef);
          //console.log('Firebase query executed. Document exists:', formSnap.exists());
          
          if (!formSnap.exists()) {
            console.error(`Form with ID ${formId} not found in Firestore`);
            setLoadError('Form not found');
            setIsLoading(false);
            return;
          }
          
          const formData = formSnap.data();
          //console.log('Form data retrieved:', { 
          //  id: formData.id,
          //  title: formData.title,
          //  status: formData.status,
          //  pageCount: formData.pages?.length || 0
          //});
          
          // Check if the form is published
          if (formData.status !== 'published') {
            console.error(`Form with ID ${formId} exists but is not published (status: ${formData.status})`);
            setLoadError('This form is not currently available');
            setIsLoading(false);
            return;
          }
          
          // Convert Firestore timestamps to JavaScript dates
          const processedForm = convertFirestoreTimestamps(formData) as Form;
          //console.log('Form processed successfully with startPageId:', processedForm.startPageId);
          
          if ((processedForm.responseCount || 0) >= MAX_FORM_RESPONSES) {
            setIsQuotaExceeded(true);
          }
          
          setForm(processedForm);
          setCurrentPageId(processedForm.startPageId);
        } catch (dbError) {
          //console.error('Firebase query error:', dbError);
          setLoadError('Error accessing form data. Please try again later.');
          setIsLoading(false);
        }
        
      } catch (error) {
        //console.error('Failed to load form:', error);
        if (error instanceof Error) {
        //console.error('Error details:', error.message, error.stack);
        }
        setLoadError('There was an error loading this form');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPublishedForm();
  }, [formId]);
  
  const currentPage = currentPageId 
    ? form?.pages.find(page => page.id === currentPageId) 
    : null;
  
  // Calculate progress
  const relevantPages = form?.pages.filter(isProgressPage) || [];
  const currentPageIndex = relevantPages.findIndex(page => page.id === currentPageId);
  const progress = relevantPages.length > 0 && currentPageIndex !== -1
    ? Math.round(((currentPageIndex + 1) / relevantPages.length) * 100)
    : 0;
  const showProgressBar = form?.settings?.showProgressBar === true && isProgressPage(currentPage);
  
  // Add effect to log required fields when page changes
  useEffect(() => {
    if (currentPage) {
      //console.log('=== CURRENT PAGE REQUIRED FIELDS CHECK ===');
      const { element } = currentPage;
      //console.log(`Page type: ${element.type}`);
      //console.log(`Element required: ${element.required}`);
      
      if (element.type === 'contact-info' || element.type === 'address') {
        if (element.properties?.fields && Array.isArray(element.properties.fields)) {
          const requiredFields = element.properties.fields.filter(field => 
            field.required === true || String(field.required).toLowerCase() === 'true' || field.required == true
          );
          
          //console.log(`Found ${requiredFields.length} required fields in ${element.type}:`);
          requiredFields.forEach(field => {
            //console.log(`  - ${field.label} (required: ${field.required}, type: ${typeof field.required})`);
          });
        } else {
          //console.log(`No fields array found in ${element.type} element`);
        }
      }
    }
  }, [currentPageId, currentPage]);
  
  // Add function to submit form responses to Firestore
  const submitFormResponses = async () => {
    if (!form || !formId) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      //console.log('Preparing to submit form responses:', responses);
      
      // Create a reference to the responses subcollection
      const responsesCollectionRef = collection(db, 'forms', formId, 'responses');
      
      // Format the response data with a cleaner structure
      const responseData = {
        // Form metadata
        formId: formId,
        formTitle: form.title,
        
        // Metadata about the submission
        metadata: {
          submittedAt: serverTimestamp(),
          userAgent: navigator.userAgent,
          ipAddress: null, // This would need to be captured server-side
        },
        
        // The actual answers, directly at the top level
        // Each key is the element.id, and value is the user's response
        answers: responses
      };
      
      // Use a batch write to add the response and increment the response count
      const batch = writeBatch(db);
      
      const newResponseRef = doc(collection(db, 'forms', formId, 'responses'));
      batch.set(newResponseRef, responseData);
      
      const formRefToUpdate = doc(db, 'forms', formId);
      batch.update(formRefToUpdate, {
        responseCount: increment(1)
      });
      
      await batch.commit();
      
      //console.log('Form response submitted successfully and count incremented.');
    } catch (error) {
      //console.error('Error submitting form response:', error);
      if (error instanceof Error) {
        //console.error('Error details:', error.message, error.stack);
      }
      setSubmitError('Failed to submit your response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Modify handleNext to submit responses when reaching the end screen
  const handleNext = () => {
    if (!currentPage || !currentPage.nextPageId) {
      // If this is the last page, show completion message
      if (currentPage?.element.type === 'end-screen') {
        // Submit the form responses
        submitFormResponses();
      }
      return;
    }
    
    //console.log('=== ATTEMPTING TO NAVIGATE ===');
    //console.log(`Current page: ${currentPage.element.type} (${currentPage.element.title})`);
    
    // Reset validation errors before validating
    setValidationError(null);
    
    const { element } = currentPage;
    const value = responses[element.id];
    let isValid = true;
    
    // For email fields, validate format even if not required but has content
    if (element.type === 'email' && value && String(value).trim() !== '') {
      isValid = validateResponse(element, value);
      if (!isValid) return;
    }
    
    // Always validate contact-info and address fields for required individual fields
    if (element.type === 'contact-info' || element.type === 'address') {
      //console.log(`Validating ${element.type} component`);
      
      // First check if the fields array exists and has required fields
      if (element.properties?.fields && Array.isArray(element.properties.fields)) {
        const requiredFields = element.properties.fields.filter(field => 
          field.required === true || String(field.required).toLowerCase() === 'true' || field.required == true
        );
        
        //console.log(`Found ${requiredFields.length} required fields in ${element.type}`);
        
        if (requiredFields.length > 0) {
          // Check each required field
          let hasEmptyRequiredFields = false;
          const missingFields: string[] = [];
          
          requiredFields.forEach(field => {
            const fieldId = `${element.id}-${field.label}`;
            const fieldValue = responses[fieldId] || '';
            const isEmpty = !fieldValue || fieldValue.trim() === '';
            
            //console.log(`Checking field "${field.label}" (${fieldId}): ${isEmpty ? 'EMPTY' : 'FILLED'}`);
            
            if (isEmpty) {
              hasEmptyRequiredFields = true;
              missingFields.push(field.label);
            }
          });
          
          if (hasEmptyRequiredFields) {
            //console.log('Validation failed: Missing required fields', missingFields);
            setValidationError(`Please fill in all required fields: ${missingFields.join(', ')}`);
            return; // Stop and don't proceed to next page
          }
        }
      }
      
      // If we get here with no return, either there are no required fields or all are filled
      isValid = validateResponse(element, value);
      //console.log(`Final validation result: ${isValid ? 'PASSED' : 'FAILED'}`);
      
      if (!isValid) {
        // Make sure validation error is set to trigger UI feedback
        if (!validationError) {
          setValidationError('Please fill in all required fields');
        }
        //console.log('Navigation blocked due to validation failure');
        return; // Stop and don't proceed to next page
      }
    }
    
    // Validation for other required fields
    else if (element.required && 
        element.type !== 'welcome-screen' && 
        element.type !== 'end-screen') {
      
      isValid = validateResponse(element, value);
      
      if (!isValid) {
        // If validateResponse sets a custom error message, it will be used
        // Otherwise, use default error message
        if (!validationError) {
          setValidationError('This field is required');
        }
        return;
      }
    }
    
    // Clear any validation errors
    setValidationError(null);
    
    // Get the next page
    const nextPage = form?.pages.find(page => page.id === currentPage.nextPageId);
    //console.log(`Proceeding to page: ${nextPage?.element.title}`);
    
    // If the next page is the end screen, submit the form
    if (nextPage?.element.type === 'end-screen') {
      submitFormResponses();
    }
    
    setCurrentPageId(currentPage.nextPageId);
  };
  
  // Validate response based on element type
  const validateResponse = (element: FormElement, value: any): boolean => {
    if (!element.required && (value === undefined || value === null || value === '')) return true;
    
    switch (element.type) {
      case 'short-text':
      case 'long-text':
      case 'multiple-choice':
      case 'yes-no':
      case 'email':
      default:
        return Boolean(value);
        
      case 'number':
        // First check if value exists
        if (!value && value !== 0) return false;
        
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return false;
        
        // Check min constraint if it exists
        if (element.properties?.min !== undefined) {
          const min = parseFloat(String(element.properties.min));
          if (!isNaN(min) && numValue < min) {
            setValidationError(`Value must be at least ${min}`);
            return false;
          }
        }
        
        // Check max constraint if it exists
        if (element.properties?.max !== undefined) {
          const max = parseFloat(String(element.properties.max));
          if (!isNaN(max) && numValue > max) {
            setValidationError(`Value must be at most ${max}`);
            return false;
          }
        }
        
        return true;
        
      case 'contact-info':
      case 'address':
        console.error(`[VALIDATION DEBUG] ${element.type} element properties:`, JSON.stringify(element.properties, null, 2));
        
        // Force validation for debugging: Set to false to use only real required fields
        const FORCE_VALIDATION = false;
        
        // Basic check for required fields array
        if (!element.properties?.fields || !Array.isArray(element.properties.fields)) {
          console.error('[VALIDATION DEBUG] No fields array found!');
          return true; // Consider valid if no fields defined
        }
        
        console.error(`[VALIDATION] Total fields in ${element.type}:`, element.properties.fields.length);
        
        // Log all fields with their required status
        element.properties.fields.forEach((field, index) => {
          console.error(`[VALIDATION DEBUG] Field #${index} "${field.label || 'unnamed'}":`, {
            label: field.label,
            required: field.required,
            typeOfRequired: typeof field.required,
            valueAsString: String(field.required)
          });
        });
        
        // Filter for required fields (using most permissive approach)
        const requiredFields = element.properties.fields.filter(field => {
          // Try all possible formats for required flag
          const isStrictTrue = field.required === true;
          const isLikeTrue = String(field.required).toLowerCase() === 'true';
          const isLooseTrue = field.required == true;
          
          console.error(`[VALIDATION DEBUG] Field "${field.label}" required check:`, {
            isStrictTrue,
            isLikeTrue, 
            isLooseTrue
          });
          
          // For debugging, log which check(s) matched
          if (isStrictTrue || isLikeTrue || isLooseTrue) {
            console.error(`[VALIDATION DEBUG] Field "${field.label}" is considered REQUIRED`);
          }
          
          // Use any check that works
          return isStrictTrue || isLikeTrue || isLooseTrue;
        });
        
        console.error(`[VALIDATION DEBUG] Found ${requiredFields.length} required fields:`, 
          requiredFields.map(f => f.label));
          
        // No required fields, so validation passes
        if (requiredFields.length === 0) {
          console.error('[VALIDATION DEBUG] No required fields found, validation PASSED');
          return true;
        }
        
        // Log all current responses for debugging
        console.error('[VALIDATION DEBUG] Current responses:', responses);
        
        // Set to track fields with errors
        const fieldsWithErrors: string[] = [];
        
        // Check each required field
        requiredFields.forEach(field => {
          const fieldId = `${element.id}-${field.label}`;
          const fieldValue = responses[fieldId];
          const valueString = String(fieldValue || '');
          
          console.error(`[VALIDATION DEBUG] Checking required field "${field.label}":`, {
            fieldId,
            value: fieldValue,
            valueAsString: valueString,
            isEmpty: !fieldValue || valueString.trim() === '',
            responseKeys: Object.keys(responses)
          });
          
          // Check if empty
          const isEmpty = !fieldValue || valueString.trim() === '';
          // Check email format if applicable
          const isInvalidEmail = field.type === 'email' && fieldValue && !isValidEmail(valueString);
          
          if (isEmpty || isInvalidEmail) {
            fieldsWithErrors.push(field.label);
            console.error(`[VALIDATION DEBUG] Field "${field.label}" FAILED validation - empty: ${isEmpty}, invalidEmail: ${isInvalidEmail}`);
          }
        });
        
        // Validation result
        const isValid = fieldsWithErrors.length === 0;
        console.error(`[VALIDATION DEBUG] Final validation result: ${isValid ? 'PASSED' : 'FAILED'}`);
        
        if (fieldsWithErrors.length > 0) {
          console.error(`[VALIDATION DEBUG] Fields with errors: ${fieldsWithErrors.join(', ')}`);
          setValidationError(`Please fill in all required fields: ${fieldsWithErrors.join(', ')}`);
        }
        
        return isValid;
    }
  };
  
  const handleInputChange = (elementId: string, value: any) => {
    setResponses(prev => ({
      ...prev,
      [elementId]: value,
    }));
  };
  
  // Handle text input changes with character limit validation
  const handleTextInputChange = (element: FormElement, value: string) => {
    // First store the value
    setResponses(prev => ({
      ...prev,
      [element.id]: value,
    }));
    
    // Check if maxLength is defined and if the value exceeds it
    if (element.properties?.maxLength) {
      if (value.length > Number(element.properties.maxLength)) {
        // Just for real-time feedback - this won't block navigation since we have maxLength on the input
        setValidationError(`Maximum ${element.properties.maxLength} characters allowed`);
      } else {
        // Clear validation error if it exists
        setValidationError(null);
      }
    }
  };
  
  // Helper function to validate email format
  const isValidEmail = (email: string): boolean => {
    if (!email || email.trim() === '') return true; // Empty is considered valid (will be caught by required check if needed)
    const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email.toLowerCase());
  };
  
  // Handle email input changes with format validation
  const handleEmailInputChange = (elementId: string, value: string) => {
    // First store the value
    setResponses(prev => ({
      ...prev,
      [elementId]: value,
    }));
    
    // Validate email format whenever there's a value (even if not required)
    if (value && value.trim() !== '') {
      if (!isValidEmail(value)) {
        setValidationError('Please enter a valid email address');
      } else {
        setValidationError(null);
      }
    } else {
      setValidationError(null);
    }
  };
  
  const handleBack = () => {
    if (!form || !currentPageId) return;
    
    // Find the page that points to the current page
    const previousPage = form.pages.find(page => page.nextPageId === currentPageId);
    
    if (previousPage) {
      setCurrentPageId(previousPage.id);
    }
  };
  
  const handleRestart = () => {
    // Start over
    setResponses({});
    setValidationError(null);
    if (form) {
      setCurrentPageId(form.startPageId);
    }
  };
  
  const renderElementByType = (page: FormPage) => {
    const { element } = page;
    
    switch (element.type) {
      case 'welcome-screen':
        return (
          <div className="flex flex-col justify-center">
            <h2 className="text-3xl font-bold mb-4">{element.title}</h2>
            {element.description && (
              <p className="text-lg mb-8" style={{ color: 'var(--text-color, #000000)' }}>{element.description}</p>
            )}
          </div>
        );
        
      case 'end-screen':
        return (
          <div className="flex flex-col justify-center">
            <h2 className="text-3xl font-bold mb-4">{element.title}</h2>
            {element.description && (
              <p className="text-lg mb-8" style={{ color: 'var(--text-color, #000000)' }}>{element.description}</p>
            )}
          </div>
        );
        
      case 'short-text':
      case 'long-text':
      case 'multiple-choice':
      case 'yes-no':
      case 'email':
      default:
        return (
          <div className="w-full">
            <fieldset className="w-full">
              <h2 className="text-3xl font-bold mb-4">
                {element.title} {element.required && <span className="text-red-500">*</span>}
              </h2>
              {element.description && (
                <p className="mb-4" style={{ color: 'var(--text-color, #000000)' }}>{element.description}</p>
              )}
              
              {/* Display image if available */}
              {element.properties?.image && (
                <div className="mb-8">
                  {element.properties?.imageSource === 'unsplash' && element.properties?.imageAttribution ? (
                    <a 
                      href={`${element.properties.imageAttribution.photographerProfileUrl || element.properties.imageAttribution.unsplashUrl || '#'}?utm_source=form_generator&utm_medium=referral`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={element.properties.image} 
                        alt="Question" 
                        className="max-w-full rounded-lg max-h-[300px] object-contain mx-0 hover:opacity-90 transition-opacity"
                      />
                    </a>
                  ) : (
                    <img 
                      src={element.properties.image} 
                      alt="Question" 
                      className="max-w-full rounded-lg max-h-[300px] object-contain mx-0"
                    />
                  )}
                  
                  {/* Display Unsplash attribution if available */}
                  {element.properties?.imageSource === 'unsplash' && element.properties?.imageAttribution && (
                    <p className="mt-2 text-xs text-gray-500">
                      Photo by{' '}
                      <a 
                        href={`${element.properties.imageAttribution.photographerProfileUrl || element.properties.imageAttribution.unsplashUrl || '#'}?utm_source=form_generator&utm_medium=referral`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-700 hover:underline"
                      >
                        {element.properties.imageAttribution.photographerName || 'Unsplash Photographer'}
                      </a>
                      {' '}on{' '}
                      <a 
                        href="https://unsplash.com/?utm_source=form_generator&utm_medium=referral" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-700 hover:underline"
                      >
                        Unsplash
                      </a>
                    </p>
                  )}
                </div>
              )}
              
              {/* Render input based on element type */}
              {renderInputByType(element)}
            </fieldset>
          </div>
        );
    }
  };
  
  // Function to render the appropriate input type
  const renderInputByType = (element: FormElement) => {
    // Use primary color for focus rings, or red for errors
    const errorClass = validationError && element.required ? 'border-red-500' : 'border-b';
    const focusRingStyle = {
      '--tw-ring-color': validationError && element.required ? 'rgb(239 68 68 / 0.5)' : 'var(--primary-color, #6366F1)',
      '--tw-ring-opacity': '0.5',
      backgroundColor: 'transparent',
      color: 'var(--text-color, #000000)',
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderRadius: '0',
      paddingLeft: '0',
      paddingRight: '0',
      borderBottomWidth: '1px',
      borderBottomColor: 'var(--primary-color, #6366F1)'
    } as React.CSSProperties;
    
    // Style for input fields with error state
    const errorInputStyle = {
      '--tw-ring-color': 'rgb(239 68 68 / 0.5)',
      '--tw-ring-opacity': '0.5',
      backgroundColor: 'transparent',
      color: 'var(--text-color, #000000)',
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderRadius: '0',
      paddingLeft: '0',
      paddingRight: '0',
      borderBottomWidth: '1px',
      borderBottomColor: 'rgb(239 68 68)'
    } as React.CSSProperties;
    
    // Style for checkboxes and radio buttons
    const controlStyle = {
      accentColor: 'var(--primary-color, #6366F1)',
      '--tw-ring-color': 'var(--primary-color, #6366F1)',
      '--tw-ring-opacity': '0.5'
    } as React.CSSProperties;
    
    switch (element.type) {
      case 'welcome-screen':
        return (
          <div className="flex flex-col justify-center">
            <h2 className="text-3xl font-bold mb-4">{element.title}</h2>
            {element.description && (
              <p className="text-lg mb-8" style={{ color: 'var(--text-color, #000000)' }}>{element.description}</p>
            )}
          </div>
        );
        
      case 'end-screen':
        return (
          <div className="flex flex-col justify-center">
            <h2 className="text-3xl font-bold mb-4">{element.title}</h2>
            {element.description && (
              <p className="text-lg mb-8" style={{ color: 'var(--text-color, #000000)' }}>{element.description}</p>
            )}
          </div>
        );
        
      case 'short-text':
        return (
          <div>
            <input
              type="text"
              id={element.id}
              className={`w-full p-3 border-b focus:outline-none focus:ring-0 focus:border-b-2 bg-transparent transition-all ${errorClass}`}
              placeholder="Type your answer here..."
              value={responses[element.id] || ''}
              onChange={(e) => handleTextInputChange(element, e.target.value)}
              maxLength={element.properties?.maxLength ? Number(element.properties.maxLength) : undefined}
              aria-label={element.title}
              aria-required={element.required}
              aria-invalid={Boolean(validationError && element.required)}
              style={focusRingStyle}
            />
            {element.properties?.maxLength && (
              <div className="mt-1 text-xs text-gray-500 flex justify-end">
                {(responses[element.id]?.length || 0)}/{element.properties.maxLength} characters
              </div>
            )}
            {validationError && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {validationError}
              </p>
            )}
          </div>
        );
        
      case 'long-text':
        return (
          <div>
            <textarea
              id={element.id}
              className={`w-full p-3 border-b focus:outline-none focus:ring-0 focus:border-b-2 bg-transparent transition-all ${errorClass}`}
              placeholder="Type your answer here..."
              rows={6}
              value={responses[element.id] || ''}
              onChange={(e) => handleTextInputChange(element, e.target.value)}
              maxLength={element.properties?.maxLength ? Number(element.properties.maxLength) : undefined}
              aria-label={element.title}
              aria-required={element.required}
              aria-invalid={Boolean(validationError && element.required)}
              style={focusRingStyle}
            />
            {element.properties?.maxLength && (
              <div className="mt-1 text-xs text-gray-500 flex justify-end">
                {(responses[element.id]?.length || 0)}/{element.properties.maxLength} characters
              </div>
            )}
            {validationError && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {validationError}
              </p>
            )}
          </div>
        );
        
      case 'dropdown':
        return (
          <div>
            <select
              id={element.id}
              className={`w-full p-3 border-b focus:outline-none focus:ring-0 focus:border-b-2 bg-transparent transition-all ${errorClass}`}
              value={responses[element.id] || ''}
              onChange={(e) => handleInputChange(element.id, e.target.value)}
              aria-label={element.title}
              aria-required={element.required}
              aria-invalid={Boolean(validationError && element.required)}
              style={focusRingStyle}
            >
              <option value="" disabled>{element.properties.placeholder || 'Select an option...'}</option>
              {element.properties.options?.map((option: { id: string; value: string }) => (
                <option key={option.id} value={option.value}>
                  {option.value}
                </option>
              ))}
            </select>
            {validationError && element.required && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {validationError}
              </p>
            )}
          </div>
        );
        
      case 'multiple-choice':
        return (
          <div className="space-y-3">
            {element.properties.options.map((option: { id: string; value: string }) => (
              <div key={option.id} className="flex items-center">
                {element.properties.allowMultiple ? (
                  <input
                    id={`${element.id}-${option.id}`}
                    name={`${element.id}-${option.id}`}
                    type="checkbox"
                    className={`h-5 w-5 focus:outline-none ${validationError && element.required ? 'border-red-500' : 'border-gray-300'}`}
                    checked={Array.isArray(responses[element.id]) && responses[element.id].includes(option.value)}
                    onChange={(e) => {
                      const currentValues = Array.isArray(responses[element.id]) ? responses[element.id] : [];
                      if (e.target.checked) {
                        handleInputChange(element.id, [...currentValues, option.value]);
                      } else {
                        handleInputChange(element.id, currentValues.filter((value: string) => value !== option.value));
                      }
                    }}
                    aria-label={option.value}
                    aria-required={element.required}
                    style={controlStyle}
                  />
                ) : (
                  <input
                    id={`${element.id}-${option.id}`}
                    name={element.id}
                    type="radio"
                    className={`h-5 w-5 focus:outline-none ${validationError && element.required ? 'border-red-500' : 'border-gray-300'}`}
                    value={option.value}
                    checked={responses[element.id] === option.value}
                    onChange={() => handleInputChange(element.id, option.value)}
                    aria-label={option.value}
                    aria-required={element.required}
                    style={controlStyle}
                  />
                )}
                <label
                  htmlFor={`${element.id}-${option.id}`}
                  className="ml-3 text-lg"
                  style={{ color: 'var(--text-color, #000000)' }}
                >
                  {option.value}
                </label>
              </div>
            ))}
            {validationError && element.required && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {validationError}
              </p>
            )}
          </div>
        );
        
      case 'yes-no':
        return (
          <div>
            <div className="flex space-x-4">
              <button
                type="button"
                className={`px-8 py-3 rounded-md text-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  responses[element.id] === 'yes'
                    ? 'text-white'
                    : 'bg-white border border-gray-300 hover:bg-gray-50'
                } ${validationError && element.required ? 'border-red-500' : ''}`}
                onClick={() => handleInputChange(element.id, 'yes')}
                aria-label={element.properties.yesLabel || 'Yes'}
                aria-required={element.required}
                style={responses[element.id] === 'yes' 
                  ? { backgroundColor: 'var(--primary-color, #6366F1)' } 
                  : { color: 'var(--primary-color, #6366F1)' }}
              >
                {element.properties.yesLabel || 'Yes'}
              </button>
              <button
                type="button"
                className={`px-8 py-3 rounded-md text-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  responses[element.id] === 'no'
                    ? 'text-white'
                    : 'bg-white border border-gray-300 hover:bg-gray-50'
                } ${validationError && element.required ? 'border-red-500' : ''}`}
                onClick={() => handleInputChange(element.id, 'no')}
                aria-label={element.properties.noLabel || 'No'}
                aria-required={element.required}
                style={responses[element.id] === 'no' 
                  ? { backgroundColor: 'var(--primary-color, #6366F1)' } 
                  : { color: 'var(--primary-color, #6366F1)' }}
              >
                {element.properties.noLabel || 'No'}
              </button>
            </div>
            {validationError && element.required && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {validationError}
              </p>
            )}
          </div>
        );
        
      case 'email':
        return (
          <div>
            <input
              type="email"
              id={element.id}
              className={`w-full p-3 border-b focus:outline-none focus:ring-0 focus:border-b-2 bg-transparent transition-all ${
                (validationError && element.required) || (responses[element.id] && !isValidEmail(responses[element.id]))
                  ? 'border-red-500'
                  : 'border-b'
              }`}
              placeholder="your@email.com"
              value={responses[element.id] || ''}
              onChange={(e) => handleEmailInputChange(element.id, e.target.value)}
              aria-label={element.title}
              aria-required={element.required}
              aria-invalid={Boolean(validationError)}
              style={(validationError && element.required) || (responses[element.id] && !isValidEmail(responses[element.id])) 
                ? errorInputStyle 
                : focusRingStyle}
            />
            {validationError && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {validateResponse(element, responses[element.id]) ? validationError : 'Please enter a valid email address'}
              </p>
            )}
          </div>
        );
        
      case 'number':
        // Check if min and max exist and are valid numbers
        const hasMin = element.properties?.min !== undefined && !isNaN(parseFloat(String(element.properties.min)));
        const hasMax = element.properties?.max !== undefined && !isNaN(parseFloat(String(element.properties.max)));
        const minValue = hasMin ? parseFloat(String(element.properties.min)) : undefined;
        const maxValue = hasMax ? parseFloat(String(element.properties.max)) : undefined;
        
        // Determine constraint message based on min/max
        let constraintMessage = '';
        if (hasMin && hasMax) {
          constraintMessage = `Please enter a number between ${minValue} and ${maxValue}`;
        } else if (hasMin) {
          constraintMessage = `Please enter a number greater than or equal to ${minValue}`;
        } else if (hasMax) {
          constraintMessage = `Please enter a number less than or equal to ${maxValue}`;
        }
        
        return (
          <div>
            <input
              type="number"
              id={element.id}
              className={`w-full p-3 border-b focus:outline-none focus:ring-0 focus:border-b-2 bg-transparent transition-all ${errorClass}`}
              placeholder={element.properties?.placeholder || "Enter a number"}
              value={responses[element.id] || ''}
              onChange={(e) => handleInputChange(element.id, e.target.value)}
              min={hasMin ? minValue : undefined}
              max={hasMax ? maxValue : undefined}
              aria-label={element.title}
              aria-required={element.required}
              aria-invalid={Boolean(validationError && element.required)}
              style={focusRingStyle}
              step={element.properties.step || 'any'}
              onBlur={(e) => {
                // Enforce min/max on blur
                const value = parseFloat(e.target.value);
                if (!isNaN(value)) {
                  if (hasMin && value < minValue!) {
                    handleInputChange(element.id, minValue!.toString());
                  } else if (hasMax && value > maxValue!) {
                    handleInputChange(element.id, maxValue!.toString());
                  }
                }
              }}
            />
            {constraintMessage && (
              <div className="mt-1 text-xs text-gray-500">
                {constraintMessage}
              </div>
            )}
            {validationError && element.required && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {validationError}
              </p>
            )}
          </div>
        );

      case 'date':
        return (
          <div className="relative">
            <input
              type="date"
              ref={(el) => {
                dateInputRefs.current[element.id] = el;
              }}
              id={element.id}
              className={`w-full p-3 pr-10 border-b focus:outline-none focus:ring-0 focus:border-b-2 bg-transparent transition-all ${errorClass}`}
              
              value={responses[element.id] || ''}
              onChange={(e) => handleInputChange(element.id, e.target.value)}
              aria-label={element.title}
              aria-required={element.required}
              aria-invalid={Boolean(validationError && element.required)}
              style={{
                ...focusRingStyle,
                color: 'var(--text-color)',
              }}
            />
            {/* Custom icon */}
            <div className="absolute inset-y-0 right-3 flex items-center cursor-pointer"
              onClick={() => {
                const inputEl = dateInputRefs.current[element.id];
                if (inputEl?.showPicker) {
                  inputEl.showPicker();
                } else {
                  inputEl?.focus();
                }
              }}
              >
                <CalendarDaysIcon className="w-5 h-5" style={{color: 'var(--text-color, #ffffff)'}} />
              </div>
            {validationError && element.required && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {validationError}
              </p>
            )}
          </div>
        );

      case 'phone':
        return (
          <div>
            <PhoneInputField
              id={element.id}
              value={responses[element.id] || ''}
              onChange={(value) => handleInputChange(element.id, value)}
              hasError={Boolean(validationError && element.required)}
              errorMessage="Please enter a valid phone number"
              required={element.required}
              label={element.title}
            />
          </div>
        );

      case 'website':
        return (
          <div>
            <input
              type="url"
              id={element.id}
              className={`w-full p-3 border-b focus:outline-none focus:ring-0 focus:border-b-2 bg-transparent transition-all ${errorClass}`}
              placeholder={element.properties.placeholder || 'https://example.com'}
              value={responses[element.id] || ''}
              onChange={(e) => handleInputChange(element.id, e.target.value)}
              aria-label={element.title}
              aria-required={element.required}
              aria-invalid={Boolean(validationError && element.required)}
              style={focusRingStyle}
            />
            {validationError && element.required && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {validationError}
              </p>
            )}
          </div>
        );

      case 'contact-info':
        return (
          <div className="space-y-4">
            {element.properties?.fields?.map((field: any) => {
              // Consistent field ID construction to match validation check
              const fieldId = `${element.id}-${field.label}`;
              const fieldValue = responses[fieldId] || '';
              
              // More robust check for required field using same approach as validation
              const requiredValue = field.required;
              const isRequired = requiredValue === true || requiredValue === 'true' || requiredValue == true;
              
              // Only show errors if validationError is set (triggered by handleNext)
              const isEmpty = !fieldValue || String(fieldValue).trim() === '';
              const isInvalid = field.type === 'email' && fieldValue && !isValidEmail(fieldValue);
              
              // Only mark as error if validation has been triggered AND the field has an issue
              const hasError = validationError && ((isRequired && isEmpty) || isInvalid);
              
              // Error message based on validation issue
              let fieldErrorMessage = isEmpty ? "This field is required" : "Please enter a valid email address";
              
              return (
                <div key={fieldId} className="mb-3">
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-color, #000000)' }}>
                    {field.label} {isRequired && <span className="text-red-500">*</span>}
                  </label>
                  
                  <input
                    type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                    id={fieldId}
                    className={`w-full p-3 border-b focus:outline-none focus:ring-0 focus:border-b-2 bg-transparent transition-all ${
                      hasError ? 'border-red-500' : 'border-b'
                    }`}
                    placeholder={field.label}
                    value={responses[fieldId] || ''}
                    onChange={(e) => handleInputChange(fieldId, e.target.value)}
                    aria-label={field.label}
                    aria-required={isRequired}
                    aria-invalid={hasError}
                    style={hasError ? errorInputStyle : focusRingStyle}
                  />
                  {hasError && (
                    <p className="mt-1 text-sm text-red-600 flex items-start">
                      <ExclamationCircleIcon className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                      {fieldErrorMessage}
                    </p>
                  )}
                </div>
              );
            })}
            {validationError && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                Please fill in all required fields
              </p>
            )}
          </div>
        );

      case 'address':
        return (
          <div className="space-y-4">
            {element.properties?.fields?.map((field: any) => {
              // Consistent field ID construction
              const fieldId = `${element.id}-${field.label}`;
              const fieldValue = responses[fieldId] || '';
              
              // More robust check for required field using same approach as validation
              const requiredValue = field.required;
              const isRequired = requiredValue === true || requiredValue === 'true' || requiredValue == true;
              
              // Only show errors if validationError is set (triggered by handleNext)
              const isEmpty = !fieldValue || String(fieldValue).trim() === '';
              const isInvalid = field.type === 'email' && fieldValue && !isValidEmail(fieldValue);
              
              // Only mark as error if validation has been triggered AND the field has an issue
              const hasError = validationError && ((isRequired && isEmpty) || isInvalid);
              
              // Error message based on validation issue
              let fieldErrorMessage = isEmpty ? "This field is required" : "Please enter a valid email address";
              
              return (
                <div key={fieldId} className="mb-3">
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-color, #000000)' }}>
                    {field.label} {isRequired && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type={field.type === 'email' ? 'email' : 'text'}
                    id={fieldId}
                    className={`w-full p-3 border-b focus:outline-none focus:ring-0 focus:border-b-2 bg-transparent transition-all ${
                      hasError ? 'border-red-500' : 'border-b'
                    }`}
                    placeholder={field.label}
                    value={responses[fieldId] || ''}
                    onChange={(e) => handleInputChange(fieldId, e.target.value)}
                    aria-label={field.label}
                    aria-required={isRequired}
                    style={hasError ? errorInputStyle : focusRingStyle}
                  />
                  {hasError && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                      {fieldErrorMessage}
                    </p>
                  )}
                </div>
              );
            })}
            {validationError && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                Please fill in all required fields
              </p>
            )}
          </div>
        );

      case 'opinion-scale':
        const steps = element.properties?.steps || 5;
        const minLabel = element.properties?.minLabel || 'Disagree';
        const maxLabel = element.properties?.maxLabel || 'Agree';
        const midLabel = element.properties?.midLabel || '';
        const shouldShrink = steps > 7; // Determine if we need smaller buttons for large scales
        const hasMidLabel = Boolean(midLabel); // Show middle label if provided, regardless of even/odd
        const isEven = steps % 2 === 0;
        const midIndex = isEven ? steps / 2 - 0.5 : Math.floor(steps / 2);
        
        return (
          <div className="flex flex-col justify-start w-full border-gray-300 pb-4 transition-all">
            <div className="max-w-md w-full">
              {/* Scale buttons in a container that matches the label container width */}
              <div className="flex mb-2">
                {Array.from({ length: steps }).map((_, i) => (
                  <div key={i} className="flex-1 flex justify-center">
                    <button
                      type="button"
                      className={`${shouldShrink ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm'} rounded-lg border flex items-center justify-center font-medium ${
                        responses[element.id] === i + 1
                          ? 'text-white'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      } ${validationError && element.required ? 'border-red-500' : ''} transition-all`}
                      onClick={() => handleInputChange(element.id, i + 1)}
                      aria-label={`Rating ${i + 1}`}
                      aria-required={element.required}
                      style={responses[element.id] === i + 1 
                        ? { backgroundColor: 'var(--primary-color)' }  
                        : {}}
                      
                    >
                      {i + 1}
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Labels aligned with the buttons */}
              <div className="flex text-xs w-full">
                {/* First label */}
                <div className="flex-1 text-left" style={{ color: 'var(--text-color, #000000)' }}>
                  {minLabel}
                </div>
                
                {/* Middle label if applicable */}
                <div className="flex-1 text-center" style={{ color: 'var(--text-color, #000000)' }}>
                  {midLabel}
                </div>

                {/* Last label */}
                <div className="flex-1 text-right" style={{ color: 'var(--text-color, #000000)' }}>
                  {maxLabel}
                </div>
              </div>
              
              {/* Small screen only - vertical label layout as fallback */}
              <div className="md:hidden text-xs mt-3 space-y-1 w-full text-left">
                {steps > 7 && (
                  <>
                    <p style={{ color: 'var(--text-color, #000000)' }}><span className="font-medium">1:</span> {minLabel}</p>
                    {hasMidLabel && 
                      <p style={{ color: 'var(--text-color, #000000)' }}><span className="font-medium">{isEven ? `${steps/2}-${steps/2+1}` : Math.ceil(steps/2)}:</span> {midLabel}</p>
                    }
                    <p style={{ color: 'var(--text-color, #000000)' }}><span className="font-medium">{steps}:</span> {maxLabel}</p>
                  </>
                )}
              </div>
            </div>
            {validationError && element.required && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {validationError}
              </p>
            )}
          </div>
        );

      case 'rating':
        return (
          <div className="border-gray-300 pb-4 transition-all">
            <div className="flex items-center justify-start space-x-1">
              {Array.from({ length: element.properties?.steps || 5 }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className="focus:outline-none hover:scale-110 transition-all"
                  onClick={() => handleInputChange(element.id, i + 1)}
                  aria-label={`Rating ${i + 1} of ${element.properties?.steps || 5}`}
                  aria-required={element.required}
                  style={
                    responses[element.id] && i < responses[element.id]
                      ? { color: 'var(--primary-color, #6366F1)' }
                      : { color: '#D1D5DB' }
                  }
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                    <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
                  </svg>
                </button>
              ))}
            </div>
            {validationError && element.required && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {validationError}
              </p>
            )}
          </div>
        );

      // ... Add additional input types as needed

      default:
        return (
          <div className="p-4 bg-gray-100 rounded">
            <p>This question type is not supported in public view.</p>
          </div>
        );
    }
  };
  
  // Generate dynamic styles based on form settings
  const getDynamicStyles = () => {
    if (!form) return {};
    
    const primaryColor = form.settings?.theme?.primaryColor || '#6366F1';
    const backgroundColor = form.settings?.theme?.backgroundColor || '#FFFFFF';
    const fontFamily = form.settings?.theme?.fontFamily || 'Inter, sans-serif';
    
    // Get text colors - either use stored values or calculate based on auto-contrast setting
    const useAutoContrast = form.settings?.theme?.useAutoContrast !== false;
    let textColor = form.settings?.theme?.textColor || '#000000';
    let primaryTextColor = form.settings?.theme?.primaryTextColor || '#FFFFFF';
    
    // If auto-contrast is enabled, calculate text colors based on background/primary colors
    if (useAutoContrast) {
      // Import dynamically to avoid circular dependencies
      const { getContrastTextColor } = require('../utils/colorUtils');
      textColor = getContrastTextColor(backgroundColor);
      primaryTextColor = getContrastTextColor(primaryColor);
    }
    
    return {
      '--primary-color': primaryColor,
      '--background-color': backgroundColor,
      '--font-family': fontFamily,
      '--text-color': textColor,
      '--primary-text-color': primaryTextColor,
    } as React.CSSProperties;
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  if (!form || !currentPage) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Form not found</h1>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          style={{ 
            backgroundColor: 'var(--primary-color, #6366F1)',
            color: 'var(--primary-text-color, #FFFFFF)'
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }
  
  if (submitError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100" style={getDynamicStyles()}>
        <div className="max-w-3xl mx-auto w-full">
          <div className="shadow-lg rounded-lg overflow-hidden flex flex-col"
               style={{ 
                 fontFamily: 'var(--font-family, Inter, sans-serif)',
                 backgroundColor: 'var(--background-color, #FFFFFF)',
                 color: 'var(--text-color, #000000)',
                 height: '70vh'
               }}>
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="p-8 overflow-y-auto h-full flex flex-col min-h-0">
                <div className="flex-1 flex flex-col justify-center min-h-min">
                  <h1 className="text-2xl font-bold text-red-600 mb-4">Submission Error</h1>
                  <p className="text-gray-600 mb-6">{submitError}</p>
                  <p className="text-gray-500 mb-6">Your answers have been saved. You can try again or come back later.</p>
                  <div className="flex flex-col space-y-3">
                    <button
                      onClick={submitFormResponses}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      style={{ 
                        backgroundColor: 'var(--primary-color, #6366F1)',
                        color: 'var(--primary-text-color, #FFFFFF)' 
                      }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Try Again'}
                    </button>
                    <button
                      onClick={handleRestart}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      style={{
                        backgroundColor: 'transparent',
                        color: 'var(--primary-color, #6366F1)'
                      }}
                    >
                      Start Over
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-8 py-4 bg-gray-50 border-t border-gray-200 sticky bottom-0">
              {/* Empty footer to maintain consistent layout */}
              <div className="flex justify-between">
                <div></div>
                <div></div>
              </div>
            </div>
          </div>
          
          {/* Form branding footer - only show on published forms */}
          <div className="mt-4 text-center text-gray-500 text-sm">
            Created with <a href="/" className="text-indigo-600 hover:text-indigo-500" target="_blank" rel="noopener noreferrer">Form Generator</a>
          </div>
        </div>
      </div>
    );
  }
  
  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="shadow-lg rounded-lg overflow-hidden flex flex-col"
               style={{ 
                 fontFamily: 'var(--font-family, Inter, sans-serif)',
                 backgroundColor: 'var(--background-color, #FFFFFF)',
                 color: 'var(--text-color, #000000)',
                 height: '70vh' 
               }}>
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="p-8 overflow-y-auto h-full flex flex-col min-h-0">
                <div className="flex-1 flex flex-col justify-center min-h-min">
                  <h1 className="text-2xl font-bold text-gray-800 mb-4">Form Error</h1>
                  <p className="text-gray-600 mb-6">{loadError}</p>
                  <div className="text-gray-500 mb-6 text-sm max-w-md">
                    This may be because:
                    <ul className="list-disc pl-5 mt-2">
                      <li>The form doesn't exist</li>
                      <li>The form hasn't been published yet</li>
                      <li>You don't have permission to view this form</li>
                    </ul>
                  </div>
                  <p className="text-gray-500 mb-6 text-sm">
                    If you believe this is an error, please contact the form creator.
                  </p>
                  <button
                    onClick={() => window.location.href = '/'}
                    className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    style={{ 
                      backgroundColor: 'var(--primary-color, #6366F1)',
                      color: 'var(--primary-text-color, #FFFFFF)'
                    }}
                  >
                    Return Home
                  </button>
                </div>
              </div>
            </div>
            <div className="px-8 py-4 bg-gray-50 border-t border-gray-200 sticky bottom-0">
              {/* Empty footer to maintain consistent layout */}
              <div className="flex justify-between">
                <div></div>
                <div></div>
              </div>
            </div>
          </div>
          
          {/* Form branding footer - only show on published forms */}
          <div className="mt-4 text-center text-gray-500 text-sm">
            Created with <a href="/" className="text-indigo-600 hover:text-indigo-500" target="_blank" rel="noopener noreferrer">Form Generator</a>
          </div>
        </div>
      </div>
    );
  }
  
  if (isQuotaExceeded) {
    return (
      <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8" style={getDynamicStyles()}>
        <div className="max-w-3xl mx-auto">
          <div className="shadow-lg rounded-lg overflow-hidden flex flex-col"
               style={{ 
                 fontFamily: 'var(--font-family, Inter, sans-serif)',
                 backgroundColor: 'var(--background-color, #FFFFFF)',
                 color: 'var(--text-color, #000000)',
                 height: '70vh' 
               }}>
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="p-8 overflow-y-auto h-full flex flex-col min-h-0">
                <div className="flex-1 flex flex-col justify-center min-h-min text-center items-center">
                  <div className="bg-red-100 p-4 rounded-full mb-6 mx-auto inline-flex">
                    <ExclamationCircleIcon className="h-12 w-12 text-red-600" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-800 mb-4">Quota Exceeded</h1>
                  <p className="text-xl text-gray-600 mb-8 max-w-md mx-auto">
                    This form has exceeded its limit of free quota and cannot accept any new responses.
                  </p>
                  <p className="text-gray-500 text-sm">
                    Please contact the form creator if you believe this is an error.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 text-center text-gray-500 text-sm">
            Created with <a href="/" className="text-indigo-600 hover:text-indigo-500" target="_blank" rel="noopener noreferrer">Form Generator</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8" style={getDynamicStyles()}>
      <div className="max-w-3xl mx-auto">
        <div className="shadow-lg rounded-lg overflow-hidden flex flex-col"
             style={{ 
               fontFamily: 'var(--font-family, Inter, sans-serif)',
               backgroundColor: 'var(--background-color, #FFFFFF)',
               color: 'var(--text-color, #000000)',
               height: '80vh' // Fixed height for consistent appearance
             }}>
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="p-8 overflow-y-auto h-full flex flex-col min-h-0">
              {/* This wrapper div will center short content but align to top for longer content */}
              <div className="flex-1 flex flex-col justify-center min-h-min">
                {renderElementByType(currentPage)}
              </div>
            </div>
          </div>
          
          {/* Global styles for all inputs to ensure proper focus states */}
          <style>
            {`
              input:focus, textarea:focus, select:focus {
                border-bottom-color: var(--primary-color, #6366F1) !important;
              }
            `}
          </style>
          
          {/* Progress Bar Container */}
          {showProgressBar && (
            <div className="h-0.5 bg-gray-200 w-full">
              <div 
                className="h-full bg-indigo-600 transition-all duration-300 ease-out"
                style={{ width: `${progress}%`, backgroundColor: 'var(--primary-color, #6366F1)' }}
              ></div>
            </div>
          )}

          {/* <div className="px-8 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center sticky bottom-0"> */}
          <div className="px-8 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center sticky bottom-0">
            {/* Back Button - Hide on welcome screen and end screen */}
            {currentPage && currentPage.element.type !== 'welcome-screen' && currentPage.element.type !== 'end-screen' ? (
              <button
                onClick={handleBack}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--primary-color, #6366F1)'
                }}
              >
                <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5" />
                Back
              </button>
            ) : (
              <div /> // Placeholder to maintain layout
            )}

            {/* Company Logo - Inserted Here */}
            {form?.settings?.theme?.logoUrl && (
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                {form.settings.theme.companyUrl ? (
                  <a
                    href={form.settings.theme.companyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                    aria-label="Company website"
                  >
                    <img
                      src={form.settings.theme.logoUrl}
                      alt="Company Logo"
                      className="max-h-8 object-contain"
                    />
                  </a>
                ) : (
                  <img
                    src={form.settings.theme.logoUrl}
                    alt="Company Logo"
                    className="max-h-8 object-contain"
                  />
                )}
              </div>
            )}
            {/* End Company Logo */}

            {/* Next or Submit button based on context */}
            {currentPage && currentPage.nextPageId ? (
              // Check if the next page is the end screen
              (() => {
                const nextPage = form?.pages.find(page => page.id === currentPage.nextPageId);
                const isNextPageEndScreen = nextPage?.element.type === 'end-screen';
                let buttonText = isSubmitting ? 'Processing...' : (currentPage.element.type === 'welcome-screen' ? 'Start' : 'Next');
                if (isNextPageEndScreen) {
                    buttonText = isSubmitting ? 'Submitting...' : 'Submit';
                }
                
                return (
                  <button
                    onClick={handleNext}
                    disabled={isSubmitting}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    style={{ 
                      backgroundColor: 'var(--primary-color, #6366F1)',
                      color: 'var(--primary-text-color, #FFFFFF)'
                    }}
                  >
                    {buttonText}
                    {!isSubmitting && <ArrowRightIcon className="ml-2 h-5 w-5" />}
                  </button>
                );
              })()
            ) : (
               // This block is for the last page (or potentially end screen)
               currentPage && currentPage.element.type === 'end-screen' ? (
                 // Render "Submit another response" button on the end screen
                 <button
                   onClick={handleRestart} // Call handleRestart to start over
                   disabled={isSubmitting} // Disable while initial submit might be processing
                   className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                   style={{ 
                     backgroundColor: 'var(--primary-color, #6366F1)',
                     color: 'var(--primary-text-color, #FFFFFF)'
                   }}
                 >
                   Submit another response
                   {/* Optional: Add an icon like ArrowPathIcon? */}
                 </button>
               ) : (
                 // This is the final submit button for the last content page if end screen doesn't exist (edge case)
                 <button
                   onClick={handleNext} // Still calls handleNext which triggers submission
                   disabled={isSubmitting}
                   className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                   style={{ 
                     backgroundColor: 'var(--primary-color, #6366F1)',
                     color: 'var(--primary-text-color, #FFFFFF)'
                   }}
                 >
                   {isSubmitting ? 'Submitting...' : 'Submit'} 
                   {!isSubmitting && <ArrowRightIcon className="ml-2 h-5 w-5" />}
                 </button>
               )
            )}
          </div>
        </div>
        
        {/* Form branding footer - only show on published forms */}
        <div className="mt-4 text-center text-gray-500 text-sm">
          Created with <a href="/" className="text-indigo-600 hover:text-indigo-500" target="_blank" rel="noopener noreferrer">Form Builder</a>
        </div>
      </div>
    </div>
  );
};

// Helper function to determine if a page should count towards progress
const isProgressPage = (page: FormPage | null | undefined): boolean => {
  if (!page) return false;
  return page.element.type !== 'welcome-screen' && page.element.type !== 'end-screen';
};

export default PublicFormView; 